// /app/open#code=…&to=… — where the iOS app opens a web page as its own account
// (TOM-909, cloud ADR 0441; contract §2.9).
//
// The app's web sheet keeps its own cookies, so it can hold a different account
// from the app, or none. The app asks the cloud for a one-time code and opens
// this page with it. The page takes the code out of the address bar, trades it
// at POST /v1/auth/web-handoff/redeem (which replaces this browser's session
// with the app's), and goes on to the page the app asked for.
//
// The code is single-use, so the trade runs once even when React runs the
// effect twice. It waits for the page's own session check first (see below).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api';
import { useAuth } from '../auth';
import { webHandoffRequest } from '../lib/webHandoff';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Phase = 'opening' | 'failed';

export function OpenFromAppView({ phase }: { phase: Phase }) {
  return (
    <section className="auth-panel">
      <div className="auth-card" data-testid="open-from-app" data-phase={phase}>
        {phase === 'opening' ? (
          <p className="auth-sub">Opening…</p>
        ) : (
          <>
            <h2>This link has expired</h2>
            <p className="auth-sub">Close this page and open it again from the Neomaris app.</p>
          </>
        )}
      </div>
    </section>
  );
}

export function OpenFromApp() {
  useDocumentTitle('Neomaris');
  const navigate = useNavigate();
  const { status, refresh } = useAuth();
  // Read once: the fragment leaves the address bar on the first render.
  const [request] = useState(() =>
    webHandoffRequest(typeof window === 'undefined' ? '' : window.location.hash)
  );
  const [phase, setPhase] = useState<Phase>(request === null ? 'failed' : 'opening');
  const started = useRef(false);

  useEffect(() => {
    // The code has no place in this browser's history, whatever happens next.
    if (window.location.hash !== '') {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`
      );
    }
  }, []);

  useEffect(() => {
    // Wait for the page's own session check to finish. It may renew the old
    // session, and a renewal answered after the trade would put the old
    // account's cookies back.
    if (request === null || status === 'unknown' || started.current) return;
    started.current = true;
    void (async () => {
      try {
        const answer = await api<{ to: string }>('/v1/auth/web-handoff/redeem', {
          method: 'POST',
          body: request,
        });
        // Read the new account before any page asks for it.
        await refresh();
        navigate(answer.to, { replace: true });
      } catch {
        setPhase('failed');
      }
    })();
  }, [request, status, navigate, refresh]);

  return <OpenFromAppView phase={phase} />;
}
